import React from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  startOfWeek,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  addDays,
  isAfter,
  isBefore,
} from 'date-fns';
import './Calendar.scss';
import FormTimePicker from './FormTimePicker';
import { TimeValue } from './types';

export type CalendarMode = 'start' | 'end' | 'both';

const WEEK_DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'];
const WEEKS_TO_RENDER = 6;
const DAYS_IN_WEEK = 7;

interface DayRangeState {
  isSelected: boolean;
  isStart: boolean;
  isEnd: boolean;
  isStartOfRange: boolean;
  isEndOfRange: boolean;
}

function getRangeClasses(state: DayRangeState): string {
  if (!state.isSelected && !state.isStart) return '';

  if (state.isStart && state.isEnd) return 'date-selected-single';
  if (state.isStart) return 'date-selected-start';
  if (state.isEnd) return 'date-selected-end';
  if (state.isStartOfRange && state.isEndOfRange) return 'date-in-range-full';
  if (state.isStartOfRange) return 'date-in-range-start';
  if (state.isEndOfRange) return 'date-in-range-end';
  return 'date-in-range';
}

function getDayTextClasses(isCurrentMonth: boolean, isStart: boolean, isEnd: boolean): string {
  return [
    'day-text',
    isCurrentMonth ? 'current-month' : 'other-month',
    isStart || isEnd ? 'selected-day-text' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

const WEEK_HEADER = (
  <div key="week-header" className="calendar-week-header">
    {WEEK_DAYS.map((d) => (
      <div key={d} className="calendar-weekday">
        <span className="weekday-text">{d}</span>
      </div>
    ))}
  </div>
);

interface CalendarProps {
  startDate: Date | null;
  endDate: Date | null;
  hoveredDate: Date | null;
  startDisplayDate: Date;
  onDateClick: (date: Date) => void;
  onDateHover: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  showTimeInputs?: boolean;
  mode?: CalendarMode;
  startTime?: TimeValue;
  endTime?: TimeValue;
  onStartTimeChange?: (time: TimeValue) => void;
  onEndTimeChange?: (time: TimeValue) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  startDate,
  endDate,
  hoveredDate,
  startDisplayDate,
  onDateClick,
  onDateHover,
  onMonthChange,
  mode = 'both',
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  showTimeInputs = true,
}) => {
  const handlePrevMonth = () => {
    onMonthChange(subMonths(startDisplayDate, 1));
  };

  const handleNextMonth = () => {
    onMonthChange(addMonths(startDisplayDate, 1));
  };

  const isDateInRange = (date: Date): boolean => {
    if (!startDate) return false;
    if (!isSameMonth(date, startDisplayDate)) return false;

    if (endDate) {
      return isWithinInterval(date, { start: startDate, end: endDate });
    }

    if (hoveredDate) {
      const start = isBefore(startDate, hoveredDate) ? startDate : hoveredDate;
      const end = isAfter(startDate, hoveredDate) ? startDate : hoveredDate;
      return isWithinInterval(date, { start, end });
    }

    return isSameDay(date, startDate);
  };

  const isStartDate = (date: Date): boolean =>
    startDate ? isSameDay(date, startDate) : false;

  const isEndDate = (date: Date): boolean =>
    endDate ? isSameDay(date, endDate) : false;

  const isStartOfRangeInWeek = (date: Date, weekStart: Date): boolean => {
    if (!startDate || !isSameMonth(date, startDisplayDate)) return false;
    if (isStartDate(date)) return true;

    const prevDay = addDays(date, -1);
    return isDateInRange(date) && !isDateInRange(prevDay);
  };

  const isEndOfRangeInWeek = (date: Date, weekEnd: Date): boolean => {
    if (!startDate || !isSameMonth(date, startDisplayDate)) return false;
    if (isEndDate(date)) return true;

    const nextDay = addDays(date, 1);
    return isDateInRange(date) && !isDateInRange(nextDay);
  };

  const getDayRangeState = (date: Date, weekStart: Date, weekEnd: Date): DayRangeState => ({
    isSelected: isDateInRange(date),
    isStart: isStartDate(date),
    isEnd: isEndDate(date),
    isStartOfRange: isStartOfRangeInWeek(date, weekStart),
    isEndOfRange: isEndOfRangeInWeek(date, weekEnd),
  });

  const renderDayCell = (
    date: Date,
    monthStart: Date,
    weekStart: Date,
    weekEnd: Date
  ) => {
    const cloneDay = new Date(date);
    const rangeState = getDayRangeState(date, weekStart, weekEnd);
    const isCurrentMonth = isSameMonth(date, monthStart);

    return (
      <button
        type="button"
        key={date.toISOString()}
        className={`calendar-day ${getRangeClasses(rangeState)}`}
        onClick={() => onDateClick(cloneDay)}
        onMouseEnter={() => onDateHover(cloneDay)}
        aria-label={format(date, 'MMMM d, yyyy')}
      >
        <span className={getDayTextClasses(isCurrentMonth, rangeState.isStart, rangeState.isEnd)}>
          {format(date, 'd')}
        </span>
      </button>
    );
  };

  const renderWeek = (weekStart: Date, monthStart: Date) => {
    const weekEnd = addDays(weekStart, 6);
    const weekDays: React.ReactNode[] = [];
    let date = weekStart;

    for (let i = 0; i < DAYS_IN_WEEK; i++) {
      weekDays.push(renderDayCell(date, monthStart, weekStart, weekEnd));
      date = addDays(date, 1);
    }

    return (
      <div key={weekStart.toISOString()} className="calendar-week">
        {weekDays}
      </div>
    );
  };

  const renderDays = () => {
    const monthStart = startOfMonth(startDisplayDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const weeks: React.ReactNode[] = [WEEK_HEADER];
    let weekStart = calendarStart;

    for (let weekIndex = 0; weekIndex < WEEKS_TO_RENDER; weekIndex++) {
      weeks.push(renderWeek(weekStart, monthStart));
      weekStart = addDays(weekStart, DAYS_IN_WEEK);
    }

    return weeks;
  };

  const noop = () => {};

  return (
    <div className="calendar-container">
      <div className="calendar-wrapper">
        <div className="calendar-content">
          <div className="calendar-header">
            <button className="month-nav-button" onClick={handlePrevMonth}>
              <div className="prev-icon" />
            </button>
            <span className="month-title">
              {format(startDisplayDate, 'MMMM yyyy')}
            </span>
            <button className="month-nav-button" onClick={handleNextMonth}>
              <div className="next-icon" />
            </button>
          </div>

          <div className="calendar-grid">{renderDays()}</div>

          {showTimeInputs && mode === 'both' && (
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '0 8px',
              }}
            >
              <FormTimePicker
                timeValue={startTime ?? null}
                onTimeChange={onStartTimeChange || noop}
              />
              <FormTimePicker
                timeValue={endTime ?? null}
                onTimeChange={onEndTimeChange || noop}
              />
            </div>
          )}

          {showTimeInputs && (mode === 'start' || mode === 'end') && (
            <div style={{ marginTop: '12px', textAlign: 'right' }}>
              <FormTimePicker
                timeValue={mode === 'start' ? (startTime ?? null) : (endTime ?? null)}
                onTimeChange={
                  mode === 'start'
                    ? onStartTimeChange || noop
                    : onEndTimeChange || noop
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
